import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Badge,
  Chip,
  CircularProgress
} from '@mui/material';
import NotificationSystem from './NotificationSystem';
import { useNotification } from '../hooks/useNotification';
import { useNotifications } from '../hooks/useNotifications';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

interface NavbarProps {
  onMenuClick: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { notification, showInfo } = useNotification();
  const { 
    notifications, 
    loading: notificationsLoading, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    refreshNotifications,
    formatRelativeTime 
  } = useNotifications();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
    // Atualizar notificações quando abrir o menu
    console.log('🔄 Abrindo menu de notificações, atualizando...');
    await refreshNotifications();
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    handleNotificationMenuClose();
  };

  const handleRefreshNotifications = async () => {
    await refreshNotifications();
    showInfo('Notificações atualizadas!');
  };

  const handleViewAllNotifications = () => {
    handleNotificationMenuClose();
    // Por enquanto, vamos mostrar uma mensagem
    // Em uma implementação futura, isso poderia navegar para uma página de notificações
    showInfo('Página de todas as notificações será implementada em breve!');
  };

  const handleLogoClick = () => {
    // Se o usuário é admin, vai para dashboard, senão para minhas assinaturas
    if (user?.isAdmin) {
      navigate('/');
    } else {
      navigate('/my-signatures');
    }
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
    navigate('/login');
  };

  const handleProfile = () => {
    handleProfileMenuClose();
    // Por enquanto, vamos mostrar uma mensagem ou navegar para uma página de perfil
    // Se não houver página de perfil implementada, podemos criar uma ou mostrar um modal
    showInfo('Funcionalidade de perfil será implementada em breve!');
  };

  const handleSettings = () => {
    handleProfileMenuClose();
    // Vamos navegar para uma página de configurações ou mostrar um modal
    // Por enquanto, vamos para o dashboard onde o usuário pode acessar as configurações
    navigate('/');
  };

  const isMenuOpen = Boolean(anchorEl);
  const isNotificationMenuOpen = Boolean(notificationAnchorEl);

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: '#ffffff',
        color: '#1e293b',
        boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)',
        borderBottom: '1px solid #e2e8f0'
      }}
    >
      <Toolbar sx={{ px: 3, py: 1 }}>
        {/* Menu Button */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={onMenuClick}
          sx={{ 
            mr: 2,
            '&:hover': { 
              bgcolor: 'action.hover'
            }
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Logo/Brand */}
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <Typography 
            variant="h5" 
            component="div" 
            onClick={handleLogoClick}
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.025em',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            UniSign
          </Typography>
          
          {user?.isAdmin && (
            <Chip 
              label="Admin" 
              size="small" 
              color="primary" 
              sx={{ 
                ml: 2, 
                fontSize: '0.75rem',
                height: 20,
                '& .MuiChip-label': {
                  px: 1
                }
              }} 
            />
          )}
        </Box>

        {/* Right Side Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Notifications */}
          <Tooltip title="Notificações">
            <IconButton 
              onClick={handleNotificationMenuOpen}
              color="inherit"
              sx={{ 
                '&:hover': { 
                  bgcolor: 'action.hover'
                }
              }}
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User Profile */}
          <Tooltip title="Perfil do usuário">
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{ 
                p: 0.5,
                '&:hover': { 
                  bgcolor: 'action.hover'
                }
              }}
            >
              <Avatar 
                src={user?.photo || (user?.photo_id ? `https://drive.google.com/uc?export=view&id=${user.photo_id}` : undefined)}
                onError={(e) => {
                  // Se a foto falhar ao carregar, remove o src para mostrar a inicial
                  const target = e.target as HTMLImageElement;
                  if (target) {
                    target.src = '';
                  }
                }}
                sx={{ 
                  width: 36, 
                  height: 36,
                  bgcolor: 'primary.main',
                  fontSize: '1rem',
                  fontWeight: 600,
                  border: '2px solid #e2e8f0'
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleProfileMenuClose}
          onClick={handleProfileMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: 2,
              boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e2e8f0'
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
                     {/* User Info */}
           <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
             <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
               {user?.name || 'Usuário'}
             </Typography>
             <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
               {user?.email || 'usuario@email.com'}
             </Typography>
             <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
               {user?.isAdmin ? 'Administrador' : 'Usuário'}
             </Typography>
           </Box>

          <Divider />

          {/* Menu Items */}
          <MenuItem onClick={handleProfile} sx={{ py: 1.5, px: 2 }}>
            <PersonIcon sx={{ mr: 2, fontSize: 20 }} />
            <Typography variant="body2">Meu Perfil</Typography>
          </MenuItem>

          <MenuItem onClick={handleSettings} sx={{ py: 1.5, px: 2 }}>
            <SettingsIcon sx={{ mr: 2, fontSize: 20 }} />
            <Typography variant="body2">Configurações</Typography>
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleLogout} sx={{ py: 1.5, px: 2, color: 'error.main' }}>
            <LogoutIcon sx={{ mr: 2, fontSize: 20 }} />
            <Typography variant="body2">Sair</Typography>
          </MenuItem>
        </Menu>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchorEl}
          open={isNotificationMenuOpen}
          onClose={handleNotificationMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 350,
              maxHeight: 500,
              borderRadius: 2,
              boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e2e8f0'
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Notifications Header */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Notificações {unreadCount > 0 && `(${unreadCount})`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                size="small" 
                onClick={handleRefreshNotifications}
                disabled={notificationsLoading}
                sx={{ 
                  p: 0.5,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                {notificationsLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
              {unreadCount > 0 && (
                <Typography 
                  variant="caption" 
                  color="primary" 
                  sx={{ 
                    cursor: 'pointer', 
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' }
                  }}
                  onClick={handleMarkAllAsRead}
                >
                  Marcar todas como lidas
                </Typography>
              )}
            </Box>
          </Box>

          {/* Loading State */}
          {notificationsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Nenhuma notificação
              </Typography>
            </Box>
          ) : (
            <>
              {/* Notification Items */}
              {notifications.slice(0, 5).map((notification) => (
                <MenuItem 
                  key={notification.id}
                  sx={{ 
                    py: 1.5, 
                    px: 2, 
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: notification.read ? 'transparent' : '#f8fafc',
                    '&:hover': {
                      backgroundColor: notification.read ? 'action.hover' : '#f1f5f9'
                    }
                  }}
                  onClick={async () => {
                    await markAsRead(notification.id);
                    // Não fechamos o menu aqui para permitir marcar múltiplas
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                    {/* Icon */}
                    <Box sx={{ mr: 2, mt: 0.5 }}>
                      {notification.type === 'success' && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />}
                      {notification.type === 'info' && <InfoIcon sx={{ color: 'info.main', fontSize: 20 }} />}
                      {notification.type === 'warning' && <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />}
                      {notification.type === 'error' && <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />}
                    </Box>
                    
                    {/* Content */}
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: notification.read ? 400 : 600, 
                          color: 'text.primary',
                          mb: 0.5
                        }}
                      >
                        {notification.title}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.5 }}
                      >
                        {notification.message}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: '0.7rem' }}
                      >
                        {formatRelativeTime(notification.timestamp)}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}

              <Divider />
              
              <MenuItem 
                sx={{ py: 1, px: 2, justifyContent: 'center' }}
                onClick={handleViewAllNotifications}
              >
                <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                  Ver todas as notificações
                </Typography>
              </MenuItem>
            </>
          )}
        </Menu>

        {/* Sistema de notificações padronizado */}
        <NotificationSystem
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={notification.onClose || (() => {})}
        />
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
